import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./components/ThemeProvider.tsx";
import { PrivacyModeProvider } from "./context/PrivacyModeContext.tsx";
import { routeTree } from "./routeTree.gen";
import "src/util/axios_init.ts";
import { AuthProvider, useAuth } from "src/components/AuthProvider.tsx";
import { queryClient } from "src/util/query_client";
import LoadingOverlay from "src/components/LoadingOverlay.tsx";
const CrispChat = React.lazy(() => import("src/components/CrispChat"));
const SleekPlan = React.lazy(() => import("src/components/SleekPlan"));
import MaintenanceMode from "src/components/MaintenanceMode.tsx";
import { getMaintenanceConfig, isMaintenanceExpired } from "src/config/maintenance";
import { initGlobalErrorHandlers } from "@/lib/errorLogger";
import { initEventLogger, logPageview } from "@/lib/eventLogger";

// Initialize global error handlers for unhandled errors and promise rejections
initGlobalErrorHandlers();

// Use dev favicon in development mode
if (import.meta.env.DEV) {
  const existingLink = document.querySelector("link[rel='icon']");
  if (existingLink) existingLink.remove();
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = "/favicon-dev.ico?" + Date.now(); // Cache bust
  document.head.appendChild(link);
}

// Initialize event logger for analytics
initEventLogger();

// Handle chunk loading errors during deployments
window.addEventListener('vite:preloadError', (event) => {
  window.location.reload();
});

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
  defaultPendingComponent: () => (
    <LoadingOverlay isLoading={true} classNames="h-screen w-screen" />
  ),
  defaultPreloadDelay: 0,
  defaultPreloadStaleTime: 0,
  defaultStaleTime: 0,
});

// Track pageviews on navigation
router.subscribe("onResolved", ({ toLocation }) => {
  logPageview(toLocation.pathname);
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    title: string;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function App() {
  const auth = useAuth();
  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <PrivacyModeProvider>
        <RouterProvider router={router} context={{ auth: auth }} />
        <Suspense fallback={null}>
          <CrispChat />
          <SleekPlan />
        </Suspense>
      </PrivacyModeProvider>
    </ThemeProvider>
  );
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  
  // Check for maintenance mode before rendering the app
  const maintenanceConfig = getMaintenanceConfig();
  if (maintenanceConfig.enabled && !isMaintenanceExpired(maintenanceConfig.estimatedEndTime)) {
    root.render(
      <React.StrictMode>
        <MaintenanceMode 
          message={maintenanceConfig.message}
          estimatedEndTime={maintenanceConfig.estimatedEndTime}
        />
      </React.StrictMode>,
    );
  } else {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}
